/**
 * EmailAccountSelector — Konto-Auswahl für Multi-Account E-Mail
 * 
 * Zeigt alle aktiven E-Mail-Konten als Dropdown an.
 * Wird in der KommunikationPage und im ComposeDialog verwendet.
 * 
 * FIRMENFARBEN-SYSTEM:
 * - Gutachter Koop  → GK → Blau (#2563EB)
 * - MietGo          → MG → Anthrazit-Blau (#1E3A5F)
 * - Lenneglas       → LG → Grün (#16A34A)
 * - Unfallgiganten  → UG → Rot (#DC2626)
 * - Protex          → PX → Anthrazit-Rot (#6B1D1D)
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Mail, Check, AlertCircle, Layers } from 'lucide-react';
import type { EmailAccount } from '@/hooks/useEmailAccounts';

// ─── Firmenfarben-System ──────────────────────────────────────────────────

interface CompanyBrand {
  label: string;
  color: string;
  textColor: string;
  patterns: string[];  // E-Mail-Domain-Patterns
}

const COMPANY_BRANDS: CompanyBrand[] = [
  {
    label: 'MG',
    color: '#1E3A5F',
    textColor: '#FFFFFF',
    patterns: ['mietgo'],
  },
  {
    label: 'LG',
    color: '#16A34A',
    textColor: '#FFFFFF',
    patterns: ['lenneglas'],
  },
  {
    label: 'UG',
    color: '#DC2626',
    textColor: '#FFFFFF',
    patterns: ['unfallgiganten'],
  },
  {
    label: 'PX',
    color: '#6B1D1D',
    textColor: '#FFFFFF',
    patterns: ['protex'],
  },
  // Gutachter Koop als Fallback (muss zuletzt stehen)
  {
    label: 'GK',
    color: '#2563EB',
    textColor: '#FFFFFF',
    patterns: ['gutachter-koop', 'gutachterkoop', 'koop', 'gmail'],
  },
];

/** Erkennt die Firma anhand der E-Mail-Adresse oder des Account-Namens */
export function detectCompanyBrand(email: string, accountName?: string): CompanyBrand {
  const searchStr = `${email} ${accountName || ''}`.toLowerCase();
  
  for (const brand of COMPANY_BRANDS) {
    for (const pattern of brand.patterns) {
      if (searchStr.includes(pattern)) {
        return brand;
      }
    }
  }
  
  // Fallback: Gutachter Koop (letzter Eintrag)
  return COMPANY_BRANDS[COMPANY_BRANDS.length - 1];
}

/** Gibt die Firmenfarbe für ein E-Mail-Konto zurück */
export function getCompanyColor(email: string, accountName?: string): string {
  return detectCompanyBrand(email, accountName).color;
}

/** Kurzer, lesbarer Betriebsname für die minimalistischen Chips */
export function shortBusinessLabel(email: string, accountName?: string): string {
  const s = `${email} ${accountName || ''}`.toLowerCase();
  if (s.includes('mietgo')) return 'MietGo';
  if (s.includes('lenneglas')) return 'Lenneglas';
  if (s.includes('unfallgiganten')) return 'Unfallgiganten';
  if (s.includes('protex')) return 'Protex';
  return 'Gutachter-Koop';
}

// ─── Props ────────────────────────────────────────────────────────────────

interface Props {
  accounts: EmailAccount[];
  selectedAccountId: string | null;
  onSelect: (accountId: string) => void;
  compact?: boolean;
  showLabel?: boolean;
  showAllOption?: boolean;
  className?: string;
}

export default function EmailAccountSelector({
  accounts,
  selectedAccountId,
  onSelect,
  compact = false,
  showLabel = true,
  showAllOption = false,
  className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = accounts.find(a => a.id === selectedAccountId);
  const isAllSelected = showAllOption && selectedAccountId === '__all__';
  const selectedBrand = selected ? detectCompanyBrand(selected.email, selected.name) : null;

  if (accounts.length <= 1 && !showLabel && !showAllOption) return null;

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Minimalistische Betriebe-Chips (nur im Unified Inbox) */}
      {showAllOption && accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <button
            onClick={() => onSelect('__all__')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              isAllSelected
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Alle
          </button>
          {accounts.map(acc => {
            const brand = detectCompanyBrand(acc.email, acc.name);
            const active = acc.id === selectedAccountId && !isAllSelected;
            return (
              <button
                key={acc.id}
                onClick={() => onSelect(acc.id)}
                title={acc.email}
                className="px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
                style={active
                  ? { backgroundColor: brand.color, color: brand.textColor }
                  : { backgroundColor: brand.color + '15', color: brand.color }}
              >
                {shortBusinessLabel(acc.email, acc.name)}
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-lg transition-colors ${
          compact
            ? 'px-2 py-1.5 hover:bg-gray-100 text-sm'
            : 'px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-sm w-full'
        }`}
      >
        {isAllSelected ? (
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-gradient-to-r from-blue-500 to-indigo-500" />
        ) : selectedBrand ? (
          <div
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: selectedBrand.color }}
          />
        ) : null}
        <span className={`truncate ${compact ? 'max-w-[160px]' : 'flex-1 text-left'} font-medium text-gray-700`}>
          {isAllSelected ? 'Alle Konten' : selected?.email || 'Konto wählen'}
        </span>
        {(accounts.length > 1 || showAllOption) && (
          <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        )}
      </button>

      {open && (accounts.length > 1 || showAllOption) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 z-50 py-1 min-w-[240px]">
          {showLabel && (
            <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
              E-Mail-Konto
            </div>
          )}
          {/* "Alle Konten" Option für Unified Inbox */}
          {showAllOption && (
            <>
              <button
                onClick={() => { onSelect('__all__'); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isAllSelected ? 'bg-gray-50 font-semibold' : 'hover:bg-gray-50'
                }`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <Layers className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-gray-900">Alle Konten</p>
                  <p className="text-xs text-gray-500">{accounts.length} Konten verbunden</p>
                </div>
                {isAllSelected && (
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                )}
              </button>
              <div className="mx-3 my-1 border-t border-gray-100" />
            </>
          )}
          {accounts.map(account => {
            const brand = detectCompanyBrand(account.email, account.name);
            return (
              <button
                key={account.id}
                onClick={() => { onSelect(account.id); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  account.id === selectedAccountId
                    ? 'bg-gray-50 font-semibold'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: brand.color + '15' }}
                >
                  <Mail className="w-4 h-4" style={{ color: brand.color }} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-flex items-center justify-center rounded text-[8px] font-bold px-1 py-0 leading-tight flex-shrink-0"
                      style={{ backgroundColor: brand.color, color: brand.textColor }}
                    >
                      {brand.label}
                    </span>
                    <p className="text-sm font-medium text-gray-900 truncate">{account.name}</p>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{account.email}</p>
                </div>
                {account.id === selectedAccountId && !isAllSelected && (
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                )}
                {account.last_sync_error && (
                  <span title={account.last_sync_error}><AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" /></span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Kompakte Konto-Anzeige für den Compose-Dialog
 */
export function EmailAccountBadge({ account }: { account: EmailAccount }) {
  const brand = detectCompanyBrand(account.email, account.name);
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: brand.color }}
      />
      <span className="text-sm font-semibold text-gray-800">{account.email}</span>
      <span className="text-xs text-gray-400">({account.name})</span>
    </div>
  );
}


/**
 * Konto-Badge für die Konversationsliste — zeigt welche Firma (GK/MG/LG/UG/PX)
 * 
 * Erkennt automatisch die Firma anhand der E-Mail-Adresse:
 * - gutachter-koop.de / gmail → GK (Blau)
 * - mietgo.de → MG (Anthrazit-Blau)
 * - lenneglas.de → LG (Grün)
 * - unfallgiganten.de → UG (Rot)
 * - protex → PX (Anthrazit-Rot)
 */
export function AccountIndicator({ account, size = 'sm' }: { account?: EmailAccount | null; size?: 'sm' | 'xs' }) {
  if (!account) return null;
  
  const brand = detectCompanyBrand(account.email, account.name);
  
  return (
    <span
      className={`inline-block rounded-full flex-shrink-0 ${
        size === 'xs' ? 'w-2 h-2' : 'w-2.5 h-2.5'
      }`}
      style={{ backgroundColor: brand.color }}
      title={`${account.email} (${brand.label})`}
    />
  );
}
